<template>
  <InternalLayout class="apps-list">
    <BannerSlider class="apps-list__banner" :banners="banners" />

    <InsideContent
      v-if="draftApps.length && hasAddAppPermission"
      :title="$t('APPS.DRAFT.TITLE')"
      :hint="$t('HINTS.APPS')"
      class="apps-list__draft-apps"
    >
      <CreateAppButton slot="actions" />
      <DraftAppsTable
        slot="default"
        :apps="draftApps"
        @delete-draft-app="getDraftApps"
      />
    </InsideContent>

    <InsideContent :title="$t('APPS.TITLE')" :hint="$t('HINTS.APPS')">
      <template slot="actions">
        <ShowHiddenAppsButton
          :hidden-apps="hiddenApps"
          :error="apiError"
          @unhide="getApps"
        />

        <CreateAppButton v-if="!draftApps.length && hasAddAppPermission" />
      </template>

      <CardApiError
        v-if="apiError"
        :error-text="$t('APPS.LIST.API_ERROR')"
        @retry="fetchData"
      />

      <AppsTable
        v-else
        slot="default"
        :is-loading="isLoading"
        :apps="appsTableData"
      />
    </InsideContent>
  </InternalLayout>
</template>

<script>
import InsideContent from '@@/common/layout/InsideContent';
import InternalLayout from '@@/common/layout/InternalLayout';
import CardApiError from '@@/common/CardApiError';
import AppsTable from './AppsTable';
import DraftAppsTable from './DraftAppsTable';
import CreateAppButton from './CreateAppButton';
import { APPS } from '@/store/types';
import { DraftApps, Agents } from '@/api/api';
import { mapActions, mapGetters } from 'vuex';
import layout from '@/mixins/layout';
import ShowHiddenAppsButton from './ShowHiddenAppsButton';
import BannerSlider from '@/components/common/BannerSlider.vue';
import useAuth from '@/auth';

const auth = useAuth();

export default {
  name: 'AppsList',

  components: {
    InsideContent,
    InternalLayout,
    CardApiError,
    AppsTable,
    DraftAppsTable,
    CreateAppButton,
    ShowHiddenAppsButton,
    BannerSlider,
  },

  mixins: [layout],

  data() {
    return {
      isLoading: false,
      apiError: false,
      draftApps: [],
      supportUnreadUserByPackage: {},
    };
  },

  computed: {
    ...mapGetters({ allApps: APPS }),

    appsTableData() {
      const apps = this.allApps?.filter(app => app.published !== 'H') || [];

      return apps.map(app => ({
        ...app,
        hasUnreadSupport:
          Number(this.supportUnreadUserByPackage[app.packageName] || 0) > 0,
      }));
    },

    hiddenApps() {
      return this.allApps?.filter(app => app.published === 'H') || [];
    },

    hasAddAppPermission() {
      return auth.check('addApp');
    },
    banners() {
      return [
        {
          image: '/banners/digipay.jpeg',
          link: 'https://trc.metrix.ir/t9veco/',
        },
        {
          image: '/banners/meli_gold.jpeg',
          link:
            'https://melligold.com/pwa/home?asset=gold&utm_source=cafebazaar&utm_medium=banner&utm_campaign=trust&utm_term=dev_top_banner',
        },
        {
          image: '/banners/meli_gold_gardoone.png',
          link: 'https://trc.metrix.ir/3yhvch/',
        },
        {
          image: '/banners/blue-business.jpg',
          link: 'https://cafeclick.adtrace.io/fv0m9r6',
        },
      ];
    },
  },

  created() {
    this.setPageTitle('APPS.TITLE');
    if (typeof window !== 'undefined') {
      window.addEventListener('support-ticket-seen', this.onSupportTicketSeen);
    }
    this.fetchData();
  },

  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener(
        'support-ticket-seen',
        this.onSupportTicketSeen
      );
    }
  },

  methods: {
    ...mapActions({ getApps: APPS }),
    onSupportTicketSeen() {
      this.getSupportUnreadConfig({ force: true }).catch(() => {});
    },
    fetchData() {
      this.isLoading = true;
      this.apiError = false;

      this.getSupportUnreadConfig().catch(() => {});

      return Promise.all([this.getApps(), this.getDraftApps()])
        .catch(({ message }) => {
          this.$message.error(message);
          this.apiError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getSupportUnreadConfig(options = {}) {
      return Agents.getSupportUnreadTicketConfig(options).then(
        ({ userByPackage }) => {
          this.supportUnreadUserByPackage = userByPackage || {};
        }
      );
    },

    getDraftApps() {
      if (!this.hasAddAppPermission) return;

      return DraftApps.getList().then(({ draftApps }) => {
        this.draftApps = draftApps;
      });
    },
  },
};
</script>

<style lang="scss">
.apps-list {
  &__draft-apps {
    margin-top: $global-margin;
    margin-bottom: $global-margin * 2;
  }

  &__top-banner {
    margin-bottom: $global-margin;
  }

  &__banner {
    margin-bottom: $global-margin * 5;
  }
}
</style>
